import {JSONCensusEquipment} from "../../data/models/JSONCensusEquipment";
import {getFormattedDate} from "../../../../../ReadAndWrite/ReadAndWrite/utils";
import {EquipmentWithSuggestedAction} from "./types";
import {EQ_TABLES_INDEXES, EQ_TABLES_NAMES} from "../../types";
import * as React from "react";
import {SelectInput, SelectInputOption} from "@vebox/etui-core";
import {Actions, ActionsEnum} from "../../data/models/CensusEnums";
import "./styles.scss";
import {
    JSONCensusEquipmentsActionsConfirmation,
    ManualChange,
} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";
import {Column} from "react-table";
import {AppTranslations} from "../../../../../components/Utils/translations/AppTranslations";

declare var AppTranslations: AppTranslations;

const valueOrDefault = (value: string | undefined): string => value ?? "-";

export const getActionOptions = (
    actions: Actions,
    availableActions: ActionsEnum[],
    withMultipleSelections?: boolean,
): SelectInputOption[] => [
    ...availableActions?.map((action) => (getSuggestedActionOption(actions, action))),
    ...(withMultipleSelections
        ? [
            {
                label: AppTranslations.massiveActions.censusCooler.multipleActions,
                value: ActionsEnum.MULTIPLE_SELECTIONS,
                isDisabled: true,
            },
        ]
        : []),
];

export const getSuggestedActionOption = (
    actions: Actions,
    suggestedAction: ActionsEnum,
): SelectInputOption => {
    return actions
        ? {
            label: actions?.[suggestedAction] ?? suggestedAction,
            value: suggestedAction,
        }
        : null;
};

const findManualChange = (
    confirmedAction: JSONCensusEquipmentsActionsConfirmation,
    entityGuid: string,
): ManualChange => {
    return confirmedAction?.manualChanges?.find(
        (manualChange) => manualChange.entityGuid === entityGuid,
    );
};

interface SelectActionProps {
    jsonCensusEquipment: JSONCensusEquipment;
    translatedActionsEnum: Actions;
    action: ActionsEnum;
    handleOnChange: (value: SelectInputOption, actionMeta: any) => void;
    availableActions: ActionsEnum[]
}

const SelectAction: React.FC<SelectActionProps> = ({
                                                       jsonCensusEquipment,
                                                       action,
                                                       translatedActionsEnum,
                                                       handleOnChange,
                                                       availableActions
                                                   }) => {
    const [value, setValue] = React.useState<SelectInputOption>(
        getSuggestedActionOption(translatedActionsEnum, action),
    );

    const onChange = (value: SelectInputOption, actionMeta: any) => {
        setValue(value);
        handleOnChange(value, actionMeta);
    };

    React.useEffect(() => {
        setValue(getSuggestedActionOption(translatedActionsEnum, action));
    }, [action, translatedActionsEnum]);

    return (
        <div style={{width: "250px"}}>
            <SelectInput
                label={AppTranslations.massiveActions.censusCooler.actionToApply}
                name={"select-" + jsonCensusEquipment?.equipmentInfo?.guid}
                options={getActionOptions(
                    translatedActionsEnum,
                    availableActions
                )}
                defaultValue={getSuggestedActionOption(
                    translatedActionsEnum,
                    jsonCensusEquipment?.suggestedAction,
                )}
                value={value}
                size={"small"}
                styles={{input: {height: "37px"}}}
                onChange={onChange}
            />
        </div>
    );
};

export const createDatasource = (
    list: JSONCensusEquipment[],
    translatedActionsEnum: Actions,
    confirmedAction: JSONCensusEquipmentsActionsConfirmation,
    addOrRemoveAndMergeManualChange: (
        action: JSONCensusEquipmentsActionsConfirmation,
        manualChange: ManualChange,
    ) => void,
    isMixedActionSelected: boolean,
    availableActions: ActionsEnum[],
): EquipmentWithSuggestedAction[] => {
    return list?.map((jsonCensusEquipment: JSONCensusEquipment) => {
        const equipment = jsonCensusEquipment.equipmentInfo;
        const action: ActionsEnum = (
            isMixedActionSelected
                ? (findManualChange(confirmedAction, jsonCensusEquipment.guid)
                    ?.action ?? confirmedAction?.action)
                : confirmedAction?.action
        ) as ActionsEnum;

        const handleOnChange = (value: SelectInputOption, actionMeta: any) => {
            const manualChange: ManualChange = {
                entityGuid: jsonCensusEquipment.guid,
                action: value.value as ActionsEnum,
            };
            addOrRemoveAndMergeManualChange(confirmedAction, manualChange);
        };
        const eq: EquipmentWithSuggestedAction = {
            code: valueOrDefault(equipment?.code),
            name: valueOrDefault(equipment?.name),
            model: valueOrDefault(equipment?.model),
            location: valueOrDefault(equipment.location),
            since: valueOrDefault(
                getFormattedDate({dateToFormat: equipment?.since}),
            ),
            action: availableActions && availableActions.length > 1 ? (
                <SelectAction
                    translatedActionsEnum={translatedActionsEnum}
                    action={action}
                    jsonCensusEquipment={jsonCensusEquipment}
                    handleOnChange={handleOnChange}
                    availableActions={availableActions}
                />
            ) : "-",
        };
        return eq;
    });
};

export const createColumnsList = (
    getTableHeadersName: (name: EQ_TABLES_NAMES) => string,
): readonly Column<any>[] => {
    return [
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.NAME),
            EQ_TABLES_INDEXES.NAME,
        ),
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.CODE),
            EQ_TABLES_INDEXES.CODE,
        ),
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.MODEL),
            EQ_TABLES_INDEXES.MODEL,
        ),
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.LOCATION),
            EQ_TABLES_INDEXES.LOCATION,
        ),
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.SINCE),
            EQ_TABLES_INDEXES.SINCE,
        ),
        createColumnElement(
            getTableHeadersName(EQ_TABLES_NAMES.ACTION),
            EQ_TABLES_INDEXES.ACTION,
        ),
    ];
};

const createColumnElement = (
    title: string | EQ_TABLES_NAMES,
    dataIndex: string,
): Column<any> => {
    return {Header: title, accessor: dataIndex};
};
